import { useState, useEffect } from 'react';
import { auth } from '../database/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../database/firebase';
import toast from 'react-hot-toast';

const useCurrentUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async firebaseUser => {
      if (firebaseUser) {
        try {
          const applicationSnap = await getDocs(
            query(collection(firestore, 'applications'), where('email', '==', firebaseUser.email))
          );

          if (applicationSnap.size > 0) {
            const application = applicationSnap.docs[0].data();
            if (application.status === 'pending' || application.status === 'rejected') {
              setApplicationStatus(application.status);
              setUser({
                id: firebaseUser.uid,
                email: firebaseUser.email,
                role: 'pending',
                applicationStatus: application.status,
              });
              setLoading(false);
              return;
            }
          }

          const adminDoc = await getDoc(doc(firestore, 'admin', firebaseUser.uid));
          if (adminDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'admin',
              ...adminDoc.data(),
            });
            setLoading(false);
            return;
          }
          const alumniDoc = await getDoc(doc(firestore, 'alumni', firebaseUser.uid));
          if (alumniDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'alumni',
              ...alumniDoc.data(),
            });
            setLoading(false);
            return;
          }
          const moderatorDoc = await getDoc(doc(firestore, 'moderator', firebaseUser.uid));
          if (moderatorDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'moderator',
              ...moderatorDoc.data(),
            });
            setLoading(false);
            return;
          }
          // Check if user is a delegated user (coach employee)
          const delegatedUserDoc = await getDoc(doc(firestore, 'delegatedUsers', firebaseUser.uid));
          if (delegatedUserDoc.exists()) {
            const delegatedData = delegatedUserDoc.data();
            // Load parent coach data
            const coachDoc = await getDoc(doc(firestore, 'coaches', delegatedData.coachId));
            if (coachDoc.exists()) {
              const coachData = coachDoc.data();
              setUser({
                id: delegatedData.coachId,
                email: coachData.email || delegatedData.coachEmail,
                role: 'coach',
                ...coachData,
                isDelegatedUser: true,
                delegatedUserId: firebaseUser.uid,
                delegatedUserEmail: firebaseUser.email,
                permissions: delegatedData.permissions,
              });
              setLoading(false);
              return;
            }
          }

          // Check if user is a client employee
          const clientEmployeeDoc = await getDoc(
            doc(firestore, 'clientEmployees', firebaseUser.uid)
          );
          if (clientEmployeeDoc.exists()) {
            const employeeData = clientEmployeeDoc.data();
            // Load parent client data
            const clientDoc = await getDoc(doc(firestore, 'clients', employeeData.clientId));
            if (clientDoc.exists()) {
              const clientData = clientDoc.data();
              setUser({
                id: employeeData.clientId,
                email: clientData.email || employeeData.clientEmail,
                role: 'client',
                ...clientData,
                isEmployeeUser: true,
                employeeUserId: firebaseUser.uid,
                employeeUserEmail: firebaseUser.email,
                permissions: employeeData.permissions,
              });
              setLoading(false);
              return;
            }
          }

          const clientDoc = await getDoc(doc(firestore, 'clients', firebaseUser.uid));
          if (clientDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'client',
              ...clientDoc.data(),
            });
            setLoading(false);
            return;
          }
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            role: 'user',
          });
        } catch (error) {
          toast.error('Error fetching user data:', error);
        }
      } else {
        setUser(null);
        setApplicationStatus(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading, applicationStatus };
};

export default useCurrentUser;
